import { useState, useEffect, useRef, useMemo } from "react";
import styles from "./Style.module.css";
import SearchBar from "../../Search/SearchBar";
import useClickOutside from "../../../Utils/Hooks/useClickOutside";
import { useGlobalContext } from "../../../Context/GlobalContext";
import useKeyboardNavigation from "../../../Utils/Hooks/useKeyboardNavigation";

const TherapistDropdown = ({
	options,
	defaultOption,
	onSelect,
	selectedLabel,
	dropdownClass,
	placeholder = "Select an option",
	placeholderClass = styles.placeholder,
	showGenderCircle = false,
	onDropdownToggle, // added optional callback
}) => {
	const {therapistLoading} = useGlobalContext();
	const [selectedOption, setSelectedOption] = useState(defaultOption);
	const [isDropdownOpen, setDropdownOpen] = useState(false);
	const [searchServiceQuery, setSearchServiceQuery] = useState(""); // ✅ Added state
	const dropdownRef = useRef(null);
	const dropdownMenuRef = useRef(null); // This serves as the listRef for keyboard nav
	const searchInputRef = useRef(null);

	// console.log("options on therapist dropdown", options);
	


	useEffect(() => {
		setSelectedOption(selectedLabel || defaultOption);
	}, [selectedLabel, defaultOption]);


	const onClose = () => {
		setDropdownOpen(false);
		if (typeof onDropdownToggle === "function") onDropdownToggle(false);
		setSearchServiceQuery("");
	};

	useClickOutside(dropdownRef, onClose);

	// Auto-scroll dropdown into view
	useEffect(() => {
		if (isDropdownOpen && dropdownMenuRef.current) {
			dropdownMenuRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	}, [isDropdownOpen]);

	// Focus search input when dropdown opens
	useEffect(() => {
		if (isDropdownOpen && searchInputRef.current) {
			// Small delay to ensure the dropdown is rendered
			setTimeout(() => {
				searchInputRef.current?.focus();
				searchInputRef.current?.select();
			}, 0);
		}
	}, [isDropdownOpen]);

	const handleSelect = (option) => {
		const selected = typeof option === "string" ? option : option.label;
		setSelectedOption(selected);
		onSelect(option);
		setDropdownOpen(false);
		if (typeof onDropdownToggle === "function") onDropdownToggle(false);
		setSearchServiceQuery(""); // clear after select
	};

	const displayOption = selectedOption || placeholder;

	// ✅ Filtered options based on search
	const filteredOptions = useMemo(() => {
		return options.filter((option) => {
			const label = typeof option === "string" ? option : option.label;
			return label.toLowerCase().includes(searchServiceQuery.toLowerCase());
		});
	}, [options, searchServiceQuery]);

	// Initialize keyboard navigation
	const { 
		selectedIndex, 
		setSelectedIndex, 
		handleKeyDown: handleKeyboardNav,
		setIsKeyboardNavActive,
		isKeyboardNavActive
	} = useKeyboardNavigation(
		filteredOptions.length,
		(item) => handleSelect(item), // Use wrapper to ensure dropdown closes
		{ listRef: dropdownMenuRef }
	);

	const handleKeyDown = (e) => {
		handleKeyboardNav(e, (index) => filteredOptions[index]);
	};


	return (
		<div
			ref={dropdownRef}
			className={`${styles.dropdown} ${styles.therapistDropdown}`}
			tabIndex={0}
		>
			<div
				className={`${dropdownClass} dropdownToggle ${!selectedLabel && placeholderClass} cursor-pointer`}
				onClick={() => {
					const nextOpen = !isDropdownOpen;
					setDropdownOpen(nextOpen);
					if (typeof onDropdownToggle === "function") onDropdownToggle(nextOpen);
				}}
			>
				
					{displayOption}
		
			</div>

			{isDropdownOpen && (
				<div className={styles.dropdownContainer}>
					<SearchBar
						ref={searchInputRef}
						placeholder="Search Therapist"
						value={searchServiceQuery}
						onChange={(e) => setSearchServiceQuery(e.target.value)}
						onKeyDown={handleKeyDown}
						className={styles.sidebarSearch}
						iconClassName={styles.rightbarSearch}
					/>

					<ul className={`${styles.dropdownContainerList}`} ref={dropdownMenuRef}>
						{therapistLoading ? (
							<li className={`${styles.loading} listLoader`}>Loading..</li>
						) : filteredOptions.length > 0 ? (
							filteredOptions.map((option, index) => {
								const label = typeof option === "string" ? option : option.label;
								const isSelected = selectedIndex === index;
								return (
									<li
										key={index}
										data-key-nav-index={index}
										className={`dropdownItem ${styles.dropdownItem} ${isSelected ? styles.selectedItem : ''}`}
										onClick={() => handleSelect(option)}
										onMouseEnter={() => {
											if (!isKeyboardNavActive) {
												setSelectedIndex(index);
											}
										}}
										onMouseMove={() => {
											if (isKeyboardNavActive) {
												setIsKeyboardNavActive(false);
												setSelectedIndex(index);
											}
										}}
									>
										<div className={styles.left}>
											{showGenderCircle && option.gender && (
												<div
													className={`genderCircle ${styles.pager} ${
														option.gender.toLowerCase() === "female"
															? "female"
															: "male"
													}`}
													title={option?.pager ? option?.pager : 'N/A'}
												>
													{option.pager === "N/A" ? (
														<div style={{ fontSize: "8px" }}>N/A</div>
													) : (
														option.pager
													)}
												</div>
											)}
											<span title={label ? label : 'N/A'}>{label}</span>
										</div>

										<div className={styles.right}>
											{option?.is_available === false && (
												<span className={styles.notAvailable}>(Busy)</span>
											)}
											{option?.is_service_provided === false && (
												<span className={styles.notAvailable}>(X)</span>
											)}
										</div>
									</li>
								);
							})
						) : (
							<li className={styles.noResults}>No results found</li>
						)}
					</ul>
				</div>
			)}

		</div>
	);
};

export default TherapistDropdown;
